import { useEffect, useState } from 'react';
import { GenericAbortSignal } from 'axios';

import { useInfiniteQuery } from '@tanstack/react-query';

import AIPrompt, { AIPromptOptions } from 'models/AIPrompt';
import { Paginated } from 'models/Paginated';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export const aiPromptsQueryKeys = {
  base: 'aiPrompts' as const,
  index: (params?: Record<string, any>) => [aiPromptsQueryKeys.base, params] as const,
};

type IndexParams = {
  'displayName.distinct'?: string;
  type?: string;
  targetClassname?: string;
} & BaseIndexQueryParams;

export async function indexAiPrompts(params: IndexParams, signal?: GenericAbortSignal) {
  return http.get<Paginated<AIPromptOptions>>('/ai_prompts', { params, signal }).then((response) => ({
    ...response.data,
    data: response.data.data.map((x) => new AIPrompt(x)),
  }));
}

export const useInfiniteAiPrompts = (params: IndexParams) => {
  const [search, setSearch] = useState<string>();

  const options = useInfiniteQuery({
    queryKey: aiPromptsQueryKeys.index({ ...params, search }),
    queryFn: ({ signal, pageParam }) => indexAiPrompts({ pageSize: 10, ...params, search, page: pageParam }, signal),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      return lastPage.meta.totalPages > lastPageParam ? lastPageParam + 1 : undefined;
    },
    enabled: false,
  });

  useEffect(() => {
    if (search !== undefined) options.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return { options, onDebouncedSearch: setSearch };
};
